import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from '../../firebase.config';
import { useEnhancedCompany } from '../../contexts/EnhancedCompanyContext';
import { getCollectionForCompany } from '../../utils/dataAdapter';
import { useTableSort, SortableHeader } from '../../utils/tableSort';
import { useTablePagination } from '../../utils/tablePagination';
import PaginationControls from '../../utils/PaginationControls';
import { printReportWithLetterhead, CollapsibleQuickTotals } from './CommonComponents';
import UnifiedExportDropdown from './UnifiedExportDropdown';
import * as XLSX from 'xlsx';

const BalanceSheetReport = ({ db, userId, appId, dateRange, financialYear, selectedParty, parties, loading, setLoading, companyDetails }) => {
  const [balanceSheetData, setBalanceSheetData] = useState({ assets: [], liabilities: [] });
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id || null;
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [drillDownData, setDrillDownData] = useState([]);
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [totalSummary, setTotalSummary] = useState({
    totalAssets: 0,
    totalLiabilities: 0,
    netWorth: 0
  });
  const [netProfit, setNetProfit] = useState(0);
  const [openingBalances, setOpeningBalances] = useState({
    debtors: 0,
    creditors: 0
  });
  const [showOpeningBalanceSummary, setShowOpeningBalanceSummary] = useState(false);

  // Table sorting and pagination
  const { sortedData, sortConfig, handleSort } = useTableSort(balanceSheetData.assets, { key: 'category', direction: 'asc' });
  const pagination = useTablePagination(sortedData, 25);

  // Fetch Balance Sheet data (Lite): Cash, Bank, Debtors, Creditors, GST Payable, Capital, Current Profit
  useEffect(() => {
    const fetchBalanceSheetReport = async () => {
      if (!db || !companyId) return;
      
      setLoading(true);
      try {
        const startStr = new Date(dateRange.start).toISOString().split('T')[0];
        const endStr = new Date(dateRange.end).toISOString().split('T')[0];
        // Get sales (JS filter by date range)
        const salesSnapshot = await getDocs(getCollectionForCompany(db, { companyId }, 'salesBills'));
        const sales = salesSnapshot.docs.map(doc => {
          const d = doc.data();
          return {
            id: doc.id,
            date: d.invoiceDate || d.date,
            totalAmount: parseFloat(d.totalAmount || d.amount || 0),
            number: d.number || d.invoiceNumber,
            partyName: d.partyName || ''
          };
        }).filter(s => {
          const dt = new Date(s.date);
          return (!dateRange?.start || dt >= new Date(dateRange.start)) && (!dateRange?.end || dt <= new Date(dateRange.end));
        });

        // Get purchases (JS filter by date range)
        const purchasesSnapshot = await getDocs(getCollectionForCompany(db, { companyId }, 'purchaseBills'));
        const purchases = purchasesSnapshot.docs.map(doc => {
          const d = doc.data();
          return {
            id: doc.id,
            date: d.billDate || d.date,
            totalAmount: parseFloat(d.totalAmount || d.amount || 0),
            billNumber: d.billNumber || d.number,
            partyName: d.partyName || ''
          };
        }).filter(p => {
          const dt = new Date(p.date);
          return (!dateRange?.start || dt >= new Date(dateRange.start)) && (!dateRange?.end || dt <= new Date(dateRange.end));
        });

        // Get all payments for cash/bank balance calculation
        const paymentsSnapshot = await getDocs(getCollectionForCompany(db, { companyId }, 'payments'));
        const payments = paymentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(p => {
          const dt = new Date(p.paymentDate || p.date);
          return (!dateRange?.start || dt >= new Date(dateRange.start)) && (!dateRange?.end || dt <= new Date(dateRange.end));
        });

        // Get all expenses for outstanding expenses calculation
        const expensesSnapshot = await getDocs(getCollectionForCompany(db, { companyId }, 'expenses'));
        const expenses = expensesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(e => {
          const dt = new Date(e.date);
          const isInDateRange =
            (!dateRange?.start || dt >= new Date(dateRange.start)) &&
            (!dateRange?.end || dt <= new Date(dateRange.end));
          const isUnsettled = !e.settled;
          return isInDateRange && isUnsettled;
        });

        // Salaries (from salaryPayments)
        const salariesSnap = await getDocs(getCollectionForCompany(db, { companyId }, 'salaryPayments'));
        const salaryRows = salariesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(s => {
          const dt = new Date(s.date || s.paymentDate);
          return (!dateRange?.start || dt >= new Date(dateRange.start)) && (!dateRange?.end || dt <= new Date(dateRange.end));
        });

        // Get all parties for opening balance calculation
        const partiesSnapshot = await getDocs(getCollectionForCompany(db, { companyId }, 'parties'));
        const parties = partiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Irregular labour/freelancer payments (only unsettled)
        const irregularSnap = await getDocs(getCollectionForCompany(db, { companyId }, 'irregularPayments'));
        const irregularRows = irregularSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(r => {
          const dt = new Date(r.date);
          const isInDateRange = (!dateRange?.start || dt >= new Date(dateRange.start)) && (!dateRange?.end || dt <= new Date(dateRange.end));
          const isUnsettled = !r.settled; // Only include unsettled advances
          return isInDateRange && isUnsettled;
        });

        // Calculate Assets
        const assets = [];

        // Build Payment Mode-style mapping from raw payments
        const isCashMode = (mode) => String(mode || '').toLowerCase().includes('cash');
        const recNoUpper = (r) => (r.receiptNumber || r.number || '').toString().toUpperCase();
        // Check for PRP first (more specific), then PRI
        // Handle cases where receiptNumber might be "PRI PRP25-26/18" - prioritize PRP
        const isSalesReceipt = (r) => {
          const rec = recNoUpper(r);
          const hasPRP = rec.includes('PRP') && (rec.startsWith('PRP') || rec.includes(' PRP'));
          if (hasPRP) return false; // PRP is purchase payment, not sales receipt
          return rec.startsWith('PRI') || (String(r.type||'').toLowerCase()==='receipt' && ((r.documentType||'').toString().toLowerCase()==='sale'));
        };
        const normalizedPayments = payments.map(p => ({ id:p.id, date:p.paymentDate || p.date, amount:Number(p.totalAmount || p.amount || 0), mode:p.paymentMode || '', partyName:p.partyName || p.partyFirmName || '', receiptNumber:p.receiptNumber || p.number || '', type:p.type, documentType:p.documentType }));
        const salesReceipts = normalizedPayments.filter(isSalesReceipt);
        const cashSales = salesReceipts.filter(r => isCashMode(r.mode));
        const bankSales = salesReceipts.filter(r => !isCashMode(r.mode));
        const cashInHand = cashSales.reduce((s,r)=>s+(r.amount||0),0);
        const cashInBank = bankSales.reduce((s,r)=>s+(r.amount||0),0);

        assets.push({
          category: 'Cash in Hand',
          amount: Math.max(0, cashInHand),
          type: 'current',
          drillDownData: cashSales.map(r => ({ id:r.id, date:r.date, amount:r.amount, partyName:r.partyName, paymentNumber:r.receiptNumber })),
          description: 'Total cash receipts from sales'
        });

        // Bank Balance
        assets.push({
          category: 'Cash in Bank',
          amount: Math.max(0, cashInBank),
          type: 'current',
          drillDownData: bankSales.map(r => ({ id:r.id, date:r.date, amount:r.amount, partyName:r.partyName, paymentNumber:r.receiptNumber })),
          description: 'Total bank/UPI/cheque receipts from sales'
        });

        // Debtors (accounts receivable) - including party opening balances
        const totalSales = sales.reduce((sum, sale) => sum + sale.totalAmount, 0);
        const salesPayments = salesReceipts.reduce((s, p) => s + (p.amount||0), 0);
        
        // Separate opening balances from current year transactions
        const partyOpeningBalances = parties.reduce((sum, party) => {
            const balance = party.openingBalance || 0;
            return sum + (balance > 0 ? balance : 0); // Only positive balances (receivable)
        }, 0);
        
        // Update opening balances state for rendering
        setOpeningBalances(prev => ({ ...prev, debtors: partyOpeningBalances }));
        
        const currentYearDebtors = totalSales - salesPayments;
        const debtors = currentYearDebtors + partyOpeningBalances;

        // Add Opening Balance/Last Year Balance section for Debtors
        if (partyOpeningBalances > 0) {
          assets.push({
            category: 'Opening Balance/Last Year Balance',
            amount: partyOpeningBalances,
            type: 'current',
            drillDownData: parties
              .filter(party => (party.openingBalance || 0) > 0)
              .map(party => ({ 
                id: party.id, 
                date: 'Opening Balance', 
                amount: party.openingBalance, 
                invoiceNumber: 'OB', 
                partyName: party.firmName || party.partyName || 'Unknown Party',
                type: 'Opening Balance',
                description: `Opening balance for ${party.firmName || party.partyName || 'Unknown Party'}`,
                entryDate: party.openingBalanceDate || 'Not specified',
                balanceType: 'Debtor (Receivable)',
                remarks: party.openingBalanceRemarks || 'Opening balance carried forward'
              })),
            description: 'Opening balances and last financial year debtors balance carried forward'
          });
        }

        // Current year debtors (excluding opening balance)
        if (currentYearDebtors > 0) {
          assets.push({
            category: 'Debtors (Current Year)',
            amount: currentYearDebtors,
            type: 'current',
            drillDownData: sales.map(s => ({ id:s.id, date:s.date, amount:s.totalAmount, invoiceNumber:s.number || s.invoiceNumber, partyName:s.partyName, type: 'Current Year' })),
            description: 'Current year amount receivable from customers'
          });
        } else if (currentYearDebtors === 0 && partyOpeningBalances > 0) {
          // Only opening balances, no current year debtors
          assets.push({
            category: 'Debtors (Current Year)',
            amount: 0,
            type: 'current',
            drillDownData: [],
            description: 'No current year sales - only opening balances exist'
          });
        } else if (currentYearDebtors === 0 && partyOpeningBalances === 0) {
          // No debtors at all
          assets.push({
            category: 'Debtors (Current Year)',
            amount: 0,
            type: 'current',
            drillDownData: [],
            description: 'No debtors or opening balances'
          });
        }

        // Fixed Assets
        const fixedAssetsSnapshot = await getDocs(getCollectionForCompany(db, { companyId }, 'fixedAssets'));
        const fixedAssets = fixedAssetsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const totalFixedAssets = fixedAssets.reduce((sum, asset) => sum + (asset.currentValue || asset.purchasePrice || 0), 0);
        if (totalFixedAssets > 0) {
          assets.push({
            category: 'Fixed Assets',
            amount: totalFixedAssets,
            type: 'fixed',
            drillDownData: fixedAssets.map(a => ({
              id: a.id,
              date: a.purchaseDate,
              amount: a.currentValue || a.purchasePrice || 0,
              billNumber: a.assetName || '',
              partyName: a.category || '',
              type: 'Fixed Asset',
              description: a.description || ''
            })),
            description: 'Fixed assets with depreciation'
          });
        }

        // Investments
        const investmentsSnapshot = await getDocs(getCollectionForCompany(db, { companyId }, 'investments'));
        const investments = investmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const totalInvestments = investments.reduce((sum, inv) => sum + (inv.currentValue || inv.amount || 0), 0);
        if (totalInvestments > 0) {
          assets.push({
            category: 'Investments',
            amount: totalInvestments,
            type: 'investment',
            drillDownData: investments.map(inv => ({
              id: inv.id,
              date: inv.investmentDate,
              amount: inv.currentValue || inv.amount || 0,
              billNumber: inv.investmentType || '',
              partyName: inv.institution || '',
              type: 'Investment',
              description: inv.description || ''
            })),
            description: 'Investments with current valuation'
          });
        }

        // Calculate Liabilities
        const liabilities = [];

        // Capital Account from simple opening (optional)
        const openingBalancesDoc = { openingCapital: 0, openingCash: 0, openingBank: 0 };
        liabilities.push({
          category: 'Capital (Opening)',
          amount: openingBalancesDoc.openingCapital || 0,
          type: 'equity',
          drillDownData: [],
          description: 'Opening capital (settings)'
        });

        // Creditors (accounts payable) - including party opening balances
        const totalPurchases = purchases.reduce((sum, purchase) => sum + purchase.totalAmount, 0);
        const purchasePaymentRows = payments
          .filter(p => {
            const rec = (p.receiptNumber || '').toString().toUpperCase();
            // Check for PRP (either at start or after space like "PRI PRP25-26/18")
            const hasPRP = rec.includes('PRP') && (rec.startsWith('PRP') || rec.includes(' PRP'));
            return hasPRP || ((p.type||'').toString().toLowerCase()==='payment' && ((p.documentType||'').toString().toLowerCase()==='purchase'));
          })
          .map(p => ({ id:p.id, date:p.paymentDate || p.date, amount:Number(p.totalAmount || p.amount || 0), receiptNumber:p.receiptNumber || p.number || '', partyName:p.partyName || p.partyFirmName || '' }));
        const purchasePayments = purchasePaymentRows.reduce((s, p) => s + Number(p.amount || 0), 0);
        
        // Separate opening balances from current year transactions
        const partyPayableBalances = parties.reduce((sum, party) => {
            const balance = party.openingBalance || 0;
            return sum + (balance < 0 ? Math.abs(balance) : 0); // Only negative balances (payable)
        }, 0);
        
        // Update opening balances state for rendering
        setOpeningBalances(prev => ({ ...prev, creditors: partyPayableBalances }));
        
        const currentYearCreditors = totalPurchases - purchasePayments;
        const creditors = currentYearCreditors + partyPayableBalances;

        // Add Opening Balance/Last Year Balance section for Creditors
        if (partyPayableBalances > 0) {
          liabilities.push({
            category: 'Opening Balance/Last Year Balance',
            amount: partyPayableBalances,
            type: 'current',
            drillDownData: parties
              .filter(party => (party.openingBalance || 0) < 0)
              .map(party => ({ 
                id: party.id, 
                date: 'Opening Balance', 
                amount: Math.abs(party.openingBalance), 
                billNumber: 'OB', 
                partyName: party.firmName || party.partyName || 'Unknown Party',
                type: 'Opening Balance',
                description: `Opening balance for ${party.firmName || party.partyName || 'Unknown Party'}`,
                entryDate: party.openingBalanceDate || 'Not specified',
                balanceType: 'Creditor (Payable)',
                remarks: party.openingBalanceRemarks || 'Opening balance carried forward'
              })),
            description: 'Opening balances and last financial year creditors balance carried forward'
          });
        }

        // Current year creditors (excluding opening balance)
        if (currentYearCreditors > 0) {
          liabilities.push({
            category: 'Creditors (Current Year)',
            amount: currentYearCreditors,
            type: 'current',
            drillDownData: purchases.map(p => ({ id:p.id, date:p.billDate || p.date, amount:p.totalAmount, billNumber:p.billNumber || p.number, partyName:p.partyName, type: 'Current Year' })),
            description: 'Current year amount payable to suppliers'
          });
        } else if (currentYearCreditors === 0 && partyPayableBalances > 0) {
          // Only opening balances, no current year creditors
          liabilities.push({
            category: 'Creditors (Current Year)',
            amount: 0,
            type: 'current',
            drillDownData: [],
            description: 'No current year purchases - only opening balances exist'
          });
        } else if (currentYearCreditors === 0 && partyPayableBalances === 0) {
          // No creditors at all
          liabilities.push({
            category: 'Creditors (Current Year)',
            amount: 0,
            type: 'current',
            drillDownData: [],
            description: 'No creditors or opening balances'
          });
        }

        // Payments made against purchase bills (allocation summary)
        if (purchasePayments > 0) {
          liabilities.push({
            category: 'Payments Made (Purchase)',
            amount: purchasePayments,
            type: 'current',
            drillDownData: purchasePaymentRows,
            description: 'Payment receipts allocated to purchase bills'
          });
        }

        // Loans
        const loansSnapshot = await getDocs(getCollectionForCompany(db, { companyId }, 'loans'));
        const loans = loansSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const totalLoans = loans.reduce((sum, loan) => sum + (loan.outstanding || loan.principal || 0), 0);
        if (totalLoans > 0) {
          liabilities.push({
            category: 'Loans',
            amount: totalLoans,
            type: 'loan',
            drillDownData: loans.map(loan => ({
              id: loan.id,
              date: loan.loanDate,
              amount: loan.outstanding || loan.principal || 0,
              billNumber: loan.loanType || '',
              partyName: loan.lender || '',
              type: 'Loan',
              description: loan.description || ''
            })),
            description: 'Outstanding loan amounts'
          });
        }

        // Outstanding Expenses
        const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
        const expensePayments = payments
          .filter(p => p.type === 'payment' && p.documentType === 'expense')
          .reduce((sum, p) => sum + p.amount, 0);
        const outstandingExpenses = totalExpenses - expensePayments;

        // Debug: Log expenses data
        console.log('Balance Sheet - Expenses Data:', {
          totalExpenses,
          expensePayments,
          outstandingExpenses,
          expensesCount: expenses.length,
          expenseDetails: expenses.map(e => ({ id: e.id, date: e.date, amount: e.amount, head: e.head, description: e.description }))
        });

        // Expenses (excluding salaries)
        const expTotal = expenses.reduce((s,e)=>s+Number(e.amount||0),0);
        if (expTotal > 0) {
          liabilities.push({
            category: 'Expenses',
            amount: expTotal,
            type: 'current',
            drillDownData: expenses.map(e => ({ id:e.id, date:e.date, amount:Number(e.amount||0), receiptNumber:e.head || e.description || '', partyName:e.head || '' })),
            description: 'Expenses excluding salaries'
          });
        }

        // Salaries
        const salTotal = salaryRows.reduce((s,e)=>s+Number(e.netAmount || e.total || 0),0);
        
        // Debug: Log salaries data
        console.log('Balance Sheet - Salaries Data:', {
          salTotal,
          salaryRowsCount: salaryRows.length,
          salaryDetails: salaryRows.map(s => ({ id: s.id, date: s.date || s.paymentDate, netAmount: s.netAmount, total: s.total, employeeName: s.employeeName }))
        });
        
        if (salTotal > 0) {
          liabilities.push({
            category: 'Salaries',
            amount: salTotal,
            type: 'current',
            drillDownData: salaryRows.map(s => ({ id:s.id, date:s.date || s.paymentDate, amount:Number(s.netAmount || s.total || 0), receiptNumber:s.employeeName || 'Salary', partyName:s.employeeName || '' })),
            description: 'Salary payments'
          });
        }

        // Labour/Advances (Irregular payments)
        const irrTotal = irregularRows.reduce((s,e)=>s+Number(e.amount || 0),0);
        
        // Debug: Log irregular payments data
        console.log('Balance Sheet - Irregular Payments Data:', {
          irrTotal,
          irregularRowsCount: irregularRows.length,
          irregularDetails: irregularRows.map(r => ({ id: r.id, date: r.date, amount: r.amount, paymentType: r.paymentType, personType: r.personType, employeeName: r.employeeName, personName: r.personName }))
        });
        
        if (irrTotal > 0) {
          liabilities.push({
            category: 'Labour/Advances',
            amount: irrTotal,
            type: 'current',
            drillDownData: irregularRows.map(r => ({ id:r.id, date:r.date, amount:Number(r.amount||0), receiptNumber:r.paymentType || 'Irregular', partyName: r.personType==='Employee' ? (r.employeeName||'Employee') : (r.personName||r.personType||'') })),
            description: 'Irregular labour/freelancer payments (advance, bonus, incentive)'
          });
        }

        // GST Payable traced from Taxes GSTR-3B cache
        const gstType = 'regular';
        const cacheKey = `gstr3b:${appId}:${userId}:${startStr}:${endStr}:${gstType}`;
        let gstrCache = null;
        try { const raw = localStorage.getItem(cacheKey); if (raw) gstrCache = JSON.parse(raw); } catch {}
        let gstPayable = 0;
        if (gstrCache?.summary) {
          const out = gstrCache.summary.outward || { cgst:0, sgst:0, igst:0 };
          const inn = gstrCache.summary.inward || { cgst:0, sgst:0, igst:0 };
          gstPayable = Math.max(0, (out.cgst - inn.cgst)) + Math.max(0, (out.sgst - inn.sgst)) + Math.max(0, (out.igst - inn.igst));
        }
        if (gstPayable > 0) {
          liabilities.push({
            category: 'GST Payable',
            amount: gstPayable,
            type: 'current',
            drillDownData: [],
            description: 'Net GST payable (from Taxes)'
          });
        }

        // Pull Net Profit from P&L cache for equity side
        let netProfit = 0;
        try {
          const plKey = `pl:${appId}:${userId}:${startStr}:${endStr}`;
          const plRaw = localStorage.getItem(plKey);
          if (plRaw) {
            const plObj = JSON.parse(plRaw);
            netProfit = plObj?.totals?.netProfit || 0;
          }
        } catch {}
        
        // Update netProfit state for UI rendering
        setNetProfit(netProfit);

        if (netProfit !== 0) {
          // In accounting: Assets = Liabilities + Equity
          // Current Profit (positive) increases equity, so add to liabilities side
          // Current Loss (negative) decreases equity, so subtract from liabilities side
          if (netProfit > 0) {
            // Profit: Add to liabilities side (increases equity)
            liabilities.push({
              category: 'Current Profit',
              amount: netProfit,
              type: 'equity',
              drillDownData: [],
              description: 'From Profit & Loss (increases equity)'
            });
          } else {
            // Loss: Subtract from liabilities side (decreases equity)
            // We'll handle this in the total calculation by reducing totalLiabilities
            console.log('Current Loss detected:', netProfit, 'will be subtracted from total liabilities');
          }
        }

        // Debug: Log all liability categories
        console.log('Balance Sheet - All Liability Categories:', liabilities.map(l => ({ category: l.category, amount: l.amount })));
        
        setBalanceSheetData({ assets, liabilities });

        // Calculate totals
        const totalAssets = assets.reduce((sum, asset) => sum + asset.amount, 0);
        let totalLiabilities = liabilities.reduce((sum, liability) => sum + liability.amount, 0);
        
        // Debug: Log totals calculation
        console.log('Balance Sheet - Totals Calculation:', {
          totalAssets,
          totalLiabilitiesBeforeLoss: totalLiabilities,
          netProfit,
          netProfitType: netProfit >= 0 ? 'Profit' : 'Loss'
        });
        
        // Handle Current Loss: subtract from total liabilities (decreases equity)
        if (netProfit < 0) {
          totalLiabilities = totalLiabilities + netProfit; // netProfit is negative, so this subtracts
          console.log('Adjusted totalLiabilities for Current Loss:', netProfit, 'New total:', totalLiabilities);
        }
        
        const netWorth = totalAssets - totalLiabilities;

        // Debug: Verify accounting equation
        console.log('Balance Sheet Accounting Equation:', {
          totalAssets,
          totalLiabilities,
          netWorth,
          equation: `${totalAssets} = ${totalLiabilities}`,
          isBalanced: Math.abs(totalAssets - totalLiabilities) < 0.01, // Allow for small rounding differences
          netProfit,
          netProfitType: netProfit >= 0 ? 'Profit' : 'Loss'
        });

        setTotalSummary({
          totalAssets,
          totalLiabilities,
          netWorth
        });

      } catch (error) {
        console.error('Error fetching balance sheet data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBalanceSheetReport();
    const onGstrUpdate = () => fetchBalanceSheetReport();
    const onPlUpdate = () => fetchBalanceSheetReport();
    window.addEventListener('gstr3b-updated', onGstrUpdate);
    window.addEventListener('pl-updated', onPlUpdate);
    return () => { window.removeEventListener('gstr3b-updated', onGstrUpdate); window.removeEventListener('pl-updated', onPlUpdate); };
  }, [db, userId, appId, dateRange, selectedParty]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN');
  };

  // Custom print function for Balance Sheet with side-by-side layout
  const printBalanceSheet = () => {
    const printWindow = window.open('', '_blank');
    
    // Build company details HTML
    const companyDetailsHTML = `
      <div class="letterhead">
        <div class="company-name">${companyDetails?.firmName || 'Company Name'}</div>
        <div class="company-details">
          ${companyDetails?.address && companyDetails?.city && companyDetails?.state ? 
            `<div>${companyDetails.address}, ${companyDetails.city}, ${companyDetails.state} ${companyDetails?.pincode || ''}</div>` : 
            companyDetails?.address ? `<div>${companyDetails.address}</div>` : ''
          }
          ${companyDetails?.gstin || companyDetails?.contactNumber || companyDetails?.email ? 
            `<div class="contact-line">
              ${companyDetails?.gstin ? `<span>GSTIN: ${companyDetails.gstin}</span>` : ''}
              ${companyDetails?.contactNumber ? `<span>Contact: ${companyDetails.contactNumber}</span>` : ''}
              ${companyDetails?.email ? `<span>Email: ${companyDetails.email}</span>` : ''}
            </div>` : ''
          }
        </div>
      </div>
    `;

    // Build report details HTML
    const reportDetailsHTML = `
      <div class="report-header">
        <h1>BALANCE SHEET REPORT</h1>
        <div class="report-info">
          <div>As on: ${formatDate(dateRange.end)}</div>
          <div>Period: ${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}</div>
        </div>
        <div class="summary-boxes">
          <div class="summary-box assets">
            <div class="label">Total Assets</div>
            <div class="amount">₹${totalSummary.totalAssets.toLocaleString('en-IN')}</div>
          </div>
          <div class="summary-box liabilities">
            <div class="label">Total Liabilities</div>
            <div class="amount">₹${totalSummary.totalLiabilities.toLocaleString('en-IN')}</div>
          </div>
          <div class="summary-box networth">
            <div class="label">Net Worth</div>
            <div class="amount">₹${totalSummary.netWorth.toLocaleString('en-IN')}</div>
          </div>
        </div>
      </div>
    `;

    // Build Assets and Liabilities side-by-side HTML
    const balanceSheetHTML = `
      <div class="balance-sheet-container">
        <div class="assets-section">
          <h2>ASSETS</h2>
          <table class="balance-table">
            <thead>
              <tr>
                <th>Assets</th>
                <th>Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              ${displayAssets.map(asset => `
                <tr>
                  <td>${asset.category}</td>
                  <td class="amount">₹${asset.amount.toLocaleString('en-IN')}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td><strong>Total Assets</strong></td>
                <td class="amount"><strong>₹${totalSummary.totalAssets.toLocaleString('en-IN')}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="liabilities-section">
          <h2>LIABILITIES</h2>
          <table class="balance-table">
            <thead>
              <tr>
                <th>Liabilities</th>
                <th>Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              ${displayLiabilities.map(liability => `
                <tr>
                  <td>${liability.category}</td>
                  <td class="amount">₹${liability.amount.toLocaleString('en-IN')}</td>
                </tr>
              `).join('')}
              ${netProfit < 0 ? `
                <tr class="loss-row">
                  <td><strong>Less: Current Loss (Reduces Equity)</strong></td>
                  <td class="amount">-₹${Math.abs(netProfit).toLocaleString('en-IN')}</td>
                </tr>
              ` : ''}
              <tr class="total-row">
                <td><strong>Total Liabilities</strong></td>
                <td class="amount"><strong>₹${totalSummary.totalLiabilities.toLocaleString('en-IN')}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    `;

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Balance Sheet Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              font-size: 12px;
              color: #333;
            }
            .letterhead { 
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 2px solid #333; 
              padding-bottom: 15px; 
            }
            .company-name { 
              font-size: 22px; 
              font-weight: bold; 
              margin-bottom: 8px; 
              color: #1f2937;
            }
            .company-details { 
              font-size: 11px; 
              color: #6b7280; 
              line-height: 1.3;
            }
            .contact-line {
              display: flex;
              justify-content: center;
              gap: 15px;
              margin-top: 3px;
            }
            .report-header {
              text-align: center;
              margin-bottom: 30px;
            }
            .report-header h1 {
              font-size: 20px;
              font-weight: bold;
              margin-bottom: 10px;
              color: #1f2937;
            }
            .report-info {
              font-size: 12px;
              color: #6b7280;
              margin-bottom: 20px;
            }
            .summary-boxes {
              display: flex;
              justify-content: center;
              gap: 20px;
              margin-bottom: 20px;
            }
            .summary-box {
              padding: 10px 15px;
              border-radius: 8px;
              text-align: center;
              min-width: 120px;
            }
            .summary-box.assets {
              background-color: #dcfce7;
              border: 1px solid #16a34a;
            }
            .summary-box.liabilities {
              background-color: #fee2e2;
              border: 1px solid #dc2626;
            }
            .summary-box.networth {
              background-color: #dbeafe;
              border: 1px solid #2563eb;
            }
            .summary-box .label {
              font-size: 10px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            .summary-box .amount {
              font-size: 14px;
              font-weight: bold;
            }
            .balance-sheet-container {
              display: flex;
              gap: 30px;
              justify-content: space-between;
            }
            .assets-section, .liabilities-section {
              flex: 1;
              max-width: 48%;
            }
            .assets-section h2, .liabilities-section h2 {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 15px;
              padding: 10px;
              border-radius: 6px;
            }
            .assets-section h2 {
              background-color: #dcfce7;
              color: #16a34a;
              border: 1px solid #16a34a;
            }
            .liabilities-section h2 {
              background-color: #fee2e2;
              color: #dc2626;
              border: 1px solid #dc2626;
            }
            .balance-table {
              width: 100%;
              border-collapse: collapse;
              border: 1px solid #ddd;
            }
            .balance-table th, .balance-table td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            .balance-table th {
              background-color: #f9fafb;
              font-weight: bold;
              font-size: 11px;
            }
            .balance-table .amount {
              text-align: right;
              font-family: 'Courier New', monospace;
            }
            .total-row {
              background-color: #f3f4f6;
              font-weight: bold;
            }
            .loss-row {
              background-color: #fef3c7;
              color: #92400e;
            }
            @media print {
              .balance-sheet-container {
                display: flex !important;
                gap: 30px !important;
              }
              .assets-section, .liabilities-section {
                flex: 1 !important;
                max-width: 48% !important;
              }
            }
          </style>
        </head>
        <body>
          ${companyDetailsHTML}
          ${reportDetailsHTML}
          ${balanceSheetHTML}
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 800);
  };

  // Custom PDF export function for Balance Sheet with side-by-side layout
  const exportBalanceSheetPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('landscape');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);
      let yPosition = margin;
      
      // Helper function to safely get text
      const safeText = (text) => text || '';
      
      // Company details
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text(safeText(companyDetails?.firmName) || 'Company Name', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 7;
      
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      if (safeText(companyDetails?.address)) {
        doc.text(safeText(companyDetails.address), pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 4;
      }
      if (safeText(companyDetails?.city)) {
        doc.text(safeText(companyDetails.city), pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 4;
      }
      if (safeText(companyDetails?.gstin)) {
        doc.text(`GSTIN: ${safeText(companyDetails.gstin)}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 4;
      }
      
      yPosition += 5;
      
      // Report title
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('BALANCE SHEET REPORT', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;
      
      // Report details
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.text(`As on: ${formatDate(dateRange.end)}`, margin, yPosition);
      doc.text(`Period: ${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`, pageWidth - margin - 60, yPosition);
      yPosition += 6;
      
      // Summary boxes
      const summaryBoxWidth = 55;
      const summaryBoxHeight = 18;
      const startX = (pageWidth - (3 * summaryBoxWidth)) / 2;
      
      // Total Assets
      doc.setFillColor(220, 252, 231);
      doc.rect(startX, yPosition, summaryBoxWidth, summaryBoxHeight, 'F');
      doc.setFontSize(7);
      doc.setFont(undefined, 'bold');
      doc.text('Total Assets', startX + summaryBoxWidth/2, yPosition + 4, { align: 'center' });
      doc.setFontSize(9);
      doc.text(`INR ${totalSummary.totalAssets.toLocaleString('en-IN')}`, startX + summaryBoxWidth/2, yPosition + 12, { align: 'center' });
      
      // Total Liabilities
      doc.setFillColor(254, 226, 226);
      doc.rect(startX + summaryBoxWidth + 10, yPosition, summaryBoxWidth, summaryBoxHeight, 'F');
      doc.setFontSize(7);
      doc.setFont(undefined, 'bold');
      doc.text('Total Liabilities', startX + summaryBoxWidth + 10 + summaryBoxWidth/2, yPosition + 4, { align: 'center' });
      doc.setFontSize(9);
      doc.text(`INR ${totalSummary.totalLiabilities.toLocaleString('en-IN')}`, startX + summaryBoxWidth + 10 + summaryBoxWidth/2, yPosition + 12, { align: 'center' });
      
      // Net Worth
      doc.setFillColor(219, 234, 254);
      doc.rect(startX + 2 * summaryBoxWidth + 20, yPosition, summaryBoxWidth, summaryBoxHeight, 'F');
      doc.setFontSize(7);
      doc.setFont(undefined, 'bold');
      doc.text('Net Worth', startX + 2 * summaryBoxWidth + 20 + summaryBoxWidth/2, yPosition + 4, { align: 'center' });
      doc.setFontSize(9);
      doc.text(`INR ${totalSummary.netWorth.toLocaleString('en-IN')}`, startX + 2 * summaryBoxWidth + 20 + summaryBoxWidth/2, yPosition + 12, { align: 'center' });
      
      yPosition += 22;
      
      // Assets and Liabilities side by side
      const columnWidth = (contentWidth - 30) / 2; // 30px gap between columns for better separation
      const leftColumnX = margin;
      const rightColumnX = margin + columnWidth + 30;
      
      // Assets Section Header
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(220, 252, 231);
      doc.rect(leftColumnX, yPosition, columnWidth, 7, 'F');
      doc.text('ASSETS', leftColumnX + columnWidth/2, yPosition + 5, { align: 'center' });
      
      // Liabilities Section Header (same Y position)
      doc.setFillColor(254, 226, 226);
      doc.rect(rightColumnX, yPosition, columnWidth, 7, 'F');
      doc.text('LIABILITIES', rightColumnX + columnWidth/2, yPosition + 5, { align: 'center' });
      
      yPosition += 10;
      
      // Assets Section Content
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      let assetsY = yPosition;
      
      displayAssets.forEach((asset, index) => {
        if (assetsY > pageHeight - 30) {
          doc.addPage();
          assetsY = margin;
        }
        doc.text(asset.category, leftColumnX + 3, assetsY);
        doc.text(`INR ${asset.amount.toLocaleString('en-IN')}`, leftColumnX + columnWidth - 3, assetsY, { align: 'right' });
        assetsY += 6;
      });
      
      // Total Assets
      doc.setFont(undefined, 'bold');
      doc.setFillColor(243, 244, 246);
      doc.rect(leftColumnX, assetsY, columnWidth, 5, 'F');
      doc.text('Total Assets', leftColumnX + 3, assetsY + 3);
      doc.text(`INR ${totalSummary.totalAssets.toLocaleString('en-IN')}`, leftColumnX + columnWidth - 3, assetsY + 3, { align: 'right' });
      assetsY += 6;
      
      // Liabilities Section Content (same Y position as Assets)
      let liabilitiesY = yPosition;
      
      displayLiabilities.forEach((liability, index) => {
        if (liabilitiesY > pageHeight - 30) {
          doc.addPage();
          liabilitiesY = margin;
        }
        doc.text(liability.category, rightColumnX + 3, liabilitiesY);
        doc.text(`INR ${liability.amount.toLocaleString('en-IN')}`, rightColumnX + columnWidth - 3, liabilitiesY, { align: 'right' });
        liabilitiesY += 6;
      });
      
      // Current Loss if applicable
      if (netProfit < 0) {
        doc.setFillColor(254, 243, 199);
        doc.rect(rightColumnX, liabilitiesY, columnWidth, 5, 'F');
        doc.text('Less: Current Loss (Reduces Equity)', rightColumnX + 3, liabilitiesY + 3);
        doc.text(`-INR ${Math.abs(netProfit).toLocaleString('en-IN')}`, rightColumnX + columnWidth - 3, liabilitiesY + 3, { align: 'right' });
        liabilitiesY += 6;
      }
      
      // Total Liabilities
      doc.setFont(undefined, 'bold');
      doc.setFillColor(243, 244, 246);
      doc.rect(rightColumnX, liabilitiesY, columnWidth, 5, 'F');
      doc.text('Total Liabilities', rightColumnX + 3, liabilitiesY + 3);
      doc.text(`INR ${totalSummary.totalLiabilities.toLocaleString('en-IN')}`, rightColumnX + columnWidth - 3, liabilitiesY + 3, { align: 'right' });
      
      // Generate filename
      const fmt = (d) => {
        try { 
          const x = new Date(d); 
          const day = String(x.getDate()); 
          const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()]; 
          const yr = x.getFullYear(); 
          return `${day}${mon}${yr}`; 
        } catch { 
          return d; 
        }
      };
      
      const filename = `BALANCE_SHEET_${fmt(dateRange.end)}.pdf`;
      doc.save(filename);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Excel export function for Balance Sheet
  const exportBalanceSheetExcel = () => {
    try {
      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      
      // Prepare data for Excel
      const excelData = [
        // Company details
        [companyDetails?.firmName || 'Company Name'],
        [companyDetails?.address || ''],
        [`GSTIN: ${companyDetails?.gstin || ''}`],
        [],
        ['BALANCE SHEET REPORT'],
        [`As on: ${formatDate(dateRange.end)}`],
        [`Period: ${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`],
        [],
        // Summary
        ['Total Assets', 'Total Liabilities', 'Net Worth'],
        [
          totalSummary.totalAssets.toLocaleString('en-IN'),
          totalSummary.totalLiabilities.toLocaleString('en-IN'),
          totalSummary.netWorth.toLocaleString('en-IN')
        ],
        [],
        // Assets and Liabilities side by side
        ['ASSETS', '', 'LIABILITIES', ''],
        ['Category', 'Amount (INR)', 'Category', 'Amount (INR)']
      ];
      
      // Add assets and liabilities data
      const maxRows = Math.max(displayAssets.length, displayLiabilities.length);
      for (let i = 0; i < maxRows; i++) {
        const asset = displayAssets[i] || { category: '', amount: 0 };
        const liability = displayLiabilities[i] || { category: '', amount: 0 };
        
        excelData.push([
          asset.category,
          asset.amount > 0 ? asset.amount.toLocaleString('en-IN') : '',
          liability.category,
          liability.amount > 0 ? liability.amount.toLocaleString('en-IN') : ''
        ]);
      }
      
      // Add Current Loss if applicable (before totals)
      if (netProfit < 0) {
        excelData.push([
          '', // Empty for assets column
          '', // Empty for assets amount column
          'Less: Current Loss (Reduces Equity)',
          `-${Math.abs(netProfit).toLocaleString('en-IN')}`
        ]);
      }
      
      // Add totals
      excelData.push([
        'Total Assets',
        totalSummary.totalAssets.toLocaleString('en-IN'),
        'Total Liabilities',
        totalSummary.totalLiabilities.toLocaleString('en-IN')
      ]);
      
      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);
      
      // Set column widths
      worksheet['!cols'] = [
        { width: 25 }, // Assets Category
        { width: 15 }, // Assets Amount
        { width: 25 }, // Liabilities Category
        { width: 15 }  // Liabilities Amount
      ];
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Balance Sheet');
      
      // Generate filename
      const fmt = (d) => {
        try { 
          const x = new Date(d); 
          const day = String(x.getDate()); 
          const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()]; 
          const yr = x.getFullYear(); 
          return `${day}${mon}${yr}`; 
        } catch { 
          return d; 
        }
      };
      
      const filename = `BALANCE_SHEET_${fmt(dateRange.end)}.xlsx`;
      XLSX.writeFile(workbook, filename);
      
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('Error generating Excel. Please try again.');
    }
  };

  // Handle category click for drill-down
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setDrillDownData(category.drillDownData || []);
    setShowDrillDown(true);
  };

  // Handle back from drill-down
  const handleBackFromDrillDown = () => {
    setShowDrillDown(false);
    setSelectedCategory(null);
    setDrillDownData([]);
  };

  // Handle drill-down row click
  const handleDrillDownRowClick = (item) => {
    console.log('Open document:', item.id, item.invoiceNumber || item.billNumber || item.expenseNumber);
    // You can implement navigation logic here
  };

  // Sample data for testing
  const sampleAssets = [
    {
      category: 'Cash in Hand',
      amount: 50000,
      type: 'current',
      drillDownData: [],
      description: 'Cash balance in hand'
    },
    {
      category: 'Bank Balance',
      amount: 100000,
      type: 'current',
      drillDownData: [],
      description: 'Bank account balance'
    },
    {
      category: 'Debtors',
      amount: 200000,
      type: 'current',
      drillDownData: [],
      description: 'Amount receivable from customers'
    },
    {
      category: 'Fixed Assets',
      amount: 200000,
      type: 'fixed',
      drillDownData: [],
      description: 'Property, plant, and equipment'
    }
  ];

  const sampleLiabilities = [
    {
      category: 'Capital Account',
      amount: 400000,
      type: 'equity',
      drillDownData: [],
      description: 'Owner\'s capital investment'
    },
    {
      category: 'Creditors',
      amount: 120000,
      type: 'current',
      drillDownData: [],
      description: 'Amount payable to suppliers'
    },
    {
      category: 'Loans',
      amount: 100000,
      type: 'long-term',
      drillDownData: [],
      description: 'Long-term borrowings'
    },
    {
      category: 'Outstanding Expenses',
      amount: 30000,
      type: 'current',
      drillDownData: [],
      description: 'Unpaid expenses'
    }
  ];

  // Use sample data if no real data
  const displayAssets = balanceSheetData.assets.length > 0 ? balanceSheetData.assets : sampleAssets;
  const displayLiabilities = balanceSheetData.liabilities.length > 0 ? balanceSheetData.liabilities : sampleLiabilities;

  

  return (
    <div className="p-3 sm:p-6">
      {/* Report Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Balance Sheet Report</h2>
            <p className="text-gray-600">
              As on: {formatDate(dateRange.end)}
              {selectedParty && ` | Party: ${parties.find(p => p.id === selectedParty)?.partyName || selectedParty}`}
            </p>
          </div>
          
          {/* Custom Balance Sheet Export/Print Buttons */}
          <div className="flex flex-wrap gap-2">
            {/* Desktop View - Full buttons */}
            {/* Unified Export Dropdown for All Views */}
            <UnifiedExportDropdown
              onPrint={printBalanceSheet}
              onExportPDF={exportBalanceSheetPDF}
              onExportExcel={exportBalanceSheetExcel}
              disabled={displayAssets.length === 0 && displayLiabilities.length === 0}
              className="flex-shrink-0"
              size="default"
            />
          </div>
        </div>
      </div>


      <CollapsibleQuickTotals
        cards={[
          {
            label: 'Total Assets',
            value: formatCurrency(totalSummary.totalAssets),
            color: 'green'
          },
          {
            label: 'Total Liabilities',
            value: formatCurrency(totalSummary.totalLiabilities),
            color: 'red'
          },
          {
            label: 'Net Worth',
            value: formatCurrency(totalSummary.netWorth),
            color: 'blue'
          }
        ]}
      />

      {/* Back Button for Drill-down */}
      {showDrillDown && (
        <div className="mb-4">
          <button
            onClick={handleBackFromDrillDown}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition duration-300"
          >
            ← Back to Balance Sheet
          </button>
        </div>
      )}

      {/* Balance Sheet Table */}
      {!showDrillDown && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Assets */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-green-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-green-800">ASSETS</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assets
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayAssets.map((asset, index) => (
                    <tr 
                      key={`asset-${index}`}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleCategoryClick(asset)}
                    >
                      <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap break-words text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <span>{asset.category}</span>
                          {asset.category === 'Opening Balance/Last Year Balance' && (
                            <div className="relative group">
                              <button className="w-4 h-4 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold hover:bg-blue-200 transition-colors">
                                i
                              </button>
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 min-w-[120px] max-w-[calc(100vw-1rem)] w-auto bg-white border border-gray-200 rounded-lg shadow-lg p-2 sm:p-3 z-[9999] opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                <div className="space-y-1">
                                  <div className="p-1.5 bg-purple-100 rounded text-center">
                                    <p className="text-[9px] text-purple-700 leading-tight break-words">
                                      <strong>Note:</strong> Last year balances
                                    </p>
                                  </div>
                                  <div className="p-1.5 bg-blue-100 rounded text-center">
                                    <p className="text-[9px] text-blue-700 font-medium break-words">
                                      <strong>Assets = Liabilities + Equity</strong>
                                    </p>
                                    <p className="text-[8px] text-blue-600 mt-0.5 leading-tight break-words">
                                      • Profit ↑ equity<br/>
                                      • Loss ↓ equity
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {asset.drillDownData && asset.drillDownData.length > 0 && (
                          <span className="ml-2 text-blue-500 text-xs">(Click to view details)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(asset.amount)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-green-100">
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-green-800">
                      Total Assets
                    </td>
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-green-800 text-right">
                      {formatCurrency(totalSummary.totalAssets)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Liabilities */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-red-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-red-800">LIABILITIES</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Liabilities
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayLiabilities.map((liability, index) => (
                    <tr 
                      key={`liability-${index}`}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleCategoryClick(liability)}
                    >
                      <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap break-words text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <span>{liability.category}</span>
                          {liability.category === 'Opening Balance/Last Year Balance' && (
                            <div className="relative group">
                              <button className="w-4 h-4 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold hover:bg-blue-200 transition-colors">
                                i
                              </button>
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 min-w-[120px] max-w-[calc(100vw-1rem)] w-auto bg-white border border-gray-200 rounded-lg shadow-lg p-2 sm:p-3 z-[9999] opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                <div className="space-y-1">
                                  <div className="p-1.5 bg-purple-100 rounded text-center">
                                    <p className="text-[9px] text-purple-700 leading-tight break-words">
                                      <strong>Note:</strong> Last year balances
                                    </p>
                                  </div>
                                  <div className="p-1.5 bg-blue-100 rounded text-center">
                                    <p className="text-[9px] text-blue-700 font-medium break-words">
                                      <strong>Assets = Liabilities + Equity</strong>
                                    </p>
                                    <p className="text-[8px] text-blue-600 mt-0.5 leading-tight break-words">
                                      • Profit ↑ equity<br/>
                                      • Loss ↓ equity
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {liability.drillDownData && liability.drillDownData.length > 0 && (
                          <span className="ml-2 text-blue-500 text-xs">(Click to view details)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(liability.amount)}
                      </td>
                    </tr>
                  ))}
                                     {/* Show Current Loss adjustment if applicable */}
                   {netProfit < 0 && (
                     <tr className="bg-orange-100">
                       <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-medium text-orange-800">
                         Less: Current Loss (Reduces Equity)
                       </td>
                       <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-medium text-orange-800 text-right">
                         -{formatCurrency(Math.abs(netProfit))}
                       </td>
                     </tr>
                   )}
                   <tr className="bg-red-100">
                     <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-red-800">
                       Total Liabilities
                     </td>
                     <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm font-bold text-red-800 text-right">
                       {formatCurrency(totalSummary.totalLiabilities)}
                     </td>
                   </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Drill-down Table */}
      {showDrillDown && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">
              {selectedCategory?.category} - Details
            </h3>
            <p className="text-sm text-gray-600 mt-1">{selectedCategory?.description}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Doc No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Party</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {drillDownData.map((item, index) => (
                  <tr 
                    key={item.id || index}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleDrillDownRowClick(item)}
                  >
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm text-gray-900">
                      {item.type === 'Opening Balance' ? (item.entryDate || 'Opening Balance') : formatDate(item.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap">
                      <div className="text-sm font-medium text-blue-600 hover:underline">
                        {item.invoiceNumber || item.billNumber || item.expenseNumber || item.paymentNumber || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm text-gray-900">
                      {item.partyName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm text-gray-900">
                      {item.type === 'Opening Balance' ? (
                        <div className="space-y-1">
                          <div className="text-xs font-medium text-purple-600">{item.balanceType}</div>
                          {item.remarks && (
                            <div className="text-xs text-gray-500">{item.remarks}</div>
                          )}
                        </div>
                      ) : (
                        item.type || 'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-normal sm:whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(item.totalAmount || item.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {displayAssets.length === 0 && displayLiabilities.length === 0 && !loading && !showDrillDown && (
        <div className="text-center py-8">
          <div className="text-gray-500 text-lg mb-2">No balance sheet data found</div>
          <p className="text-gray-400">Try adjusting your filters or date range</p>
        </div>
      )}
    </div>
  );
};

export default BalanceSheetReport; 